import React, { useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef } from "react";
import { Users, Luggage, Star, Sparkles, Shield } from "lucide-react";
import { useBookingModal } from "../contexts/BookingModalContext";
import { useTranslation } from "react-i18next";

export const VehicleFleet: React.FC = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [selectedVehicle, setSelectedVehicle] = useState(0);
  const { openModal } = useBookingModal();

  const vehicles = [
    {
      name: "Mercedes-Benz E 350 Sedan",
      type: "Saloon",
      minPrice: "€50",
      luggage: "2 pieces",
      passengers: "4 persons",
      image: "/images/Sedan.webp",
      features: [
        t("1102", "Premium Leather"),
        t("1103", "Climate Control"),
        t("1104", "WiFi"),
      ],
      gradient: "from-primary to-primary-dark",
    },
    {
      name: "Mercedes-Benz E 450 Wagon",
      type: "Station",
      minPrice: "€55",
      luggage: "3 pieces",
      passengers: "4 persons",
      image: "/images/Station.webp",
      features: [
        t("1105", "Extra Space"),
        t("1106", "Premium Sound"),
        t("1107", "Navigation"),
      ],
      gradient: "from-primary-light to-primary",
    },
    {
      name: "Mercedes-Benz V 250 D-Avantgarde",
      type: "Minivan",
      minPrice: "€80",
      luggage: "8 pieces",
      passengers: "8 persons",
      image: "/images/Minivan.webp",
      features: [
        t("1108", "Group Travel"),
        t("1109", "Luxury Interior"),
        t("1110", "Entertainment"),
      ],
      gradient: "from-accent to-primary-light",
    },
    {
      name: "Mercedes-Benz S 580 Sedan",
      type: "Saloon",
      minPrice: "€150",
      luggage: "3 pieces",
      passengers: "4 persons",
      image: "/images/S class.webp",
      features: [
        t("1111", "Ultra Luxury"),
        t("1112", "Massage Seats"),
        t("1113", "Champagne Bar"),
      ],
      gradient: "from-warning to-primary",
    },
    {
      name: "Mercedes-Benz Sprinter 519 CDI",
      type: "Minibus",
      minPrice: "€225",
      luggage: "N/A",
      passengers: "20 persons",
      image: "/images/sprint.webp",
      features: [
        t("1114", "Large Groups"),
        t("1115", "Professional Driver"),
        t("1116", "Event Transport"),
      ],
      gradient: "from-primary-dark to-primary",
    },
    {
      name: "IVECO Daily Unvi C 70",
      type: "Minibus",
      minPrice: "€225",
      luggage: "N/A",
      passengers: "20 persons",
      image: "/images/ivaco.webp",
      features: [
        t("1117", "Corporate Events"),
        t("1118", "Airport Transfers"),
        t("1119", "Tour Groups"),
      ],
      gradient: "from-accent to-primary",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
      },
    },
  };

  return (
    <section ref={ref} className="py-32 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 right-20 w-96 h-96 bg-primary/20 rounded-full opacity-30 blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-warning/20 rounded-full opacity-30 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="text-center mb-20"
        >
          <motion.div
            variants={itemVariants}
            className="inline-flex items-center space-x-2 bg-primary/20 text-primary px-6 py-3 rounded-full text-sm font-medium mb-6"
          >
            <Sparkles className="h-4 w-4" />
            <span>{t("1120", "Premium Fleet")}</span>
          </motion.div>

          <motion.h2
            variants={itemVariants}
            className="text-4xl md:text-6xl font-bold text-white mb-8"
          >
            {t("1121", "Mercedes-Benz")}{" "}
            <span className="bg-linear-to-r from-primary to-primary-light bg-clip-text text-transparent">
              {t("1122", "Excellence")}
            </span>
          </motion.h2>

          <motion.p
            variants={itemVariants}
            className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed"
          >
            {t(
              "1123",
              "Choose from our premium fleet of Mercedes-Benz vehicles, each maintained to the highest standards and equipped with luxury amenities"
            )}
          </motion.p>

          <motion.div
            variants={itemVariants}
            className="w-32 h-1 bg-linear-to-r from-primary to-primary-light mx-auto rounded-full mt-8"
          />
        </motion.div>

        {/* Vehicle Selector */}
        <motion.div
          variants={itemVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="flex flex-wrap justify-center gap-4 mb-16"
        >
          {vehicles.map((vehicle, index) => (
            <motion.button
              key={vehicle.name}
              onClick={() => setSelectedVehicle(index)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 ${
                selectedVehicle === index
                  ? `bg-linear-to-r ${vehicle.gradient} text-white shadow-lg`
                  : "bg-gray-800/50 backdrop-blur-xs text-gray-200 hover:bg-gray-700 border border-gray-600"
              }`}
            >
              {vehicle.type}
            </motion.button>
          ))}
        </motion.div>

        {/* Featured Vehicle */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedVehicle}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.5 }}
            className="bg-gray-800/50 backdrop-blur-xs rounded-3xl shadow-2xl overflow-hidden mb-16 border border-gray-700"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="relative">
                <img
                  src={vehicles[selectedVehicle].image}
                  alt={vehicles[selectedVehicle].name}
                  className="w-full h-80 lg:h-full object-cover"
                />
                <div
                  className={`absolute inset-0 bg-linear-to-r ${vehicles[selectedVehicle].gradient} opacity-20`}
                />

                {/* Price Badge */}
                <div className="absolute top-6 right-6 bg-gray-800/95 backdrop-blur-xs px-4 py-2 rounded-full">
                  <span className="text-2xl font-bold text-white">
                    {vehicles[selectedVehicle].minPrice}
                  </span>
                  <span className="text-sm text-gray-300 ml-1">
                    {t("1124", "starting")}
                  </span>
                </div>
              </div>

              <div className="p-6 sm:p-8 lg:p-12">
                <div
                  className={`inline-block px-4 py-2 bg-linear-to-r ${vehicles[selectedVehicle].gradient} text-white rounded-full text-sm font-medium mb-4`}
                >
                  {vehicles[selectedVehicle].type}
                </div>

                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-4 sm:mb-6">
                  {vehicles[selectedVehicle].name}
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-10 h-10 sm:w-12 sm:h-12 bg-linear-to-r ${vehicles[selectedVehicle].gradient} rounded-xl flex items-center justify-center`}
                    >
                      <Users className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-white">
                        {t("1125", "Passengers")}
                      </div>
                      <div className="text-gray-300">
                        {vehicles[selectedVehicle].passengers}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-10 h-10 sm:w-12 sm:h-12 bg-linear-to-r ${vehicles[selectedVehicle].gradient} rounded-xl flex items-center justify-center`}
                    >
                      <Luggage className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-white">
                        {t("1126", "Luggage")}
                      </div>
                      <div className="text-gray-300">
                        {vehicles[selectedVehicle].luggage}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-6 sm:mb-8">
                  <div className="font-semibold text-white mb-3">
                    {t("1127", "Premium Features")}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {vehicles[selectedVehicle].features.map(
                      (feature, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-gray-700 text-gray-200 rounded-full text-sm"
                        >
                          {feature}
                        </span>
                      )
                    )}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className="h-4 w-4 sm:h-5 sm:w-5 text-amber-400 fill-current"
                      />
                    ))}
                    <span className="text-gray-300 ml-2 text-sm sm:text-base">
                      {t("1128", "Premium Service")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Vehicle Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {vehicles.map((vehicle, index) => (
            <motion.div
              key={vehicle.name}
              variants={itemVariants}
              whileHover={{ y: -10, scale: 1.02 }}
              onClick={() => setSelectedVehicle(index)}
              className={`cursor-pointer bg-gray-800/50 backdrop-blur-xs rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border-2 ${
                selectedVehicle === index
                  ? `border-transparent bg-linear-to-r ${vehicle.gradient} p-0.5`
                  : "border-gray-100"
              }`}
            >
              <div
                className={`${
                  selectedVehicle === index
                    ? "bg-gray-800/50 backdrop-blur-xs rounded-2xl"
                    : ""
                } overflow-hidden`}
              >
                <div className="relative">
                  <img
                    src={vehicle.image}
                    alt={vehicle.name}
                    className="w-full h-48 object-cover"
                  />
                  <div
                    className={`absolute top-4 left-4 bg-linear-to-r ${vehicle.gradient} text-white px-3 py-1 rounded-full text-sm font-medium`}
                  >
                    {vehicle.type}
                  </div>
                  <div className="absolute top-4 right-4 bg-gray-800/95 backdrop-blur-xs text-white px-3 py-1 rounded-full text-sm font-bold">
                    {t("1129", "From")} {vehicle.minPrice}
                  </div>

                  {selectedVehicle === index && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute inset-0 bg-black/20 flex items-center justify-center"
                    >
                      <div className="bg-gray-800 rounded-full p-3">
                        <Shield className="h-6 w-6 text-green-500" />
                      </div>
                    </motion.div>
                  )}
                </div>

                <div className="p-6">
                  <h3 className="text-lg font-bold text-white mb-3">
                    {vehicle.name}
                  </h3>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center space-x-2 text-gray-300 text-sm">
                      <Users className="h-4 w-4" />
                      <span>{vehicle.passengers}</span>
                    </div>

                    <div className="flex items-center space-x-2 text-gray-300 text-sm">
                      <Luggage className="h-4 w-4" />
                      <span>{vehicle.luggage}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className="h-3 w-3 text-amber-400 fill-current"
                        />
                      ))}
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      onClick={openModal}
                      className={`bg-linear-to-r ${vehicle.gradient} text-white px-4 py-2 rounded-lg text-sm font-medium`}
                    >
                      {t("1130", "Select")}
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
